import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Plus,
  Sparkles,
  X,
} from "lucide-react";
import { GlobalButton, GlobalTextField } from "../../../../../global/forms/GlobalFormFields";
import { GlobalTableActionButton } from "../../../../../global/GlobalTableActionButton";
import { GlobalModal } from "../../../../../global/GlobalModal";
import {
  fetchAttendanceHolidays,
  createAttendanceHoliday,
  updateAttendanceHoliday,
  deleteAttendanceHoliday,
} from "../../../api/attendanceApi";

const CURRENT_YEAR = new Date().getFullYear();
const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function parseHolidayDate(iso) {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return {
    day: d.getDate(),
    month: MONTH_NAMES[d.getMonth()],
    monthIndex: d.getMonth(),
    weekday: d.toLocaleDateString(undefined, { weekday: "long" }),
    long: d.toLocaleDateString(undefined, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    }),
  };
}

function groupHolidaysByMonth(rows) {
  const groups = new Map();
  for (const row of rows) {
    const parsed = parseHolidayDate(row.holiday_date);
    const key = parsed?.monthIndex ?? 0;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  }
  for (const list of groups.values()) {
    list.sort((a, b) => String(a.holiday_date).localeCompare(String(b.holiday_date)));
  }
  return [...groups.entries()].sort(([a], [b]) => a - b);
}

export default function AttendanceHolidaysSection({ onNotify }) {
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [yearOptions, setYearOptions] = useState([CURRENT_YEAR]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    id: null,
    holiday_name: "",
    holiday_date: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const sortedYears = useMemo(
    () => [...yearOptions].sort((a, b) => b - a),
    [yearOptions],
  );

  const monthGroups = useMemo(() => groupHolidaysByMonth(rows), [rows]);

  const yearIndex = sortedYears.indexOf(selectedYear);

  const loadHolidays = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAttendanceHolidays(selectedYear);
      setRows(data.holidays ?? []);
      if (Array.isArray(data.yearOptions) && data.yearOptions.length) {
        setYearOptions(data.yearOptions);
      }
    } catch (err) {
      onNotify?.(err.message, { variant: "error" });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [onNotify, selectedYear]);

  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  const resetForm = () => {
    setForm({ id: null, holiday_name: "", holiday_date: "" });
    setIsEditing(false);
    setFormError("");
  };

  const handleEdit = (row) => {
    setForm({
      id: row.id,
      holiday_name: row.holiday_name,
      holiday_date: row.holiday_date,
    });
    setIsEditing(true);
    setFormError("");
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setFormError("");

    const holiday_name = String(form.holiday_name ?? "").trim();
    const holiday_date = String(form.holiday_date ?? "").trim();

    if (!holiday_name) {
      setFormError("Give the holiday a name.");
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(holiday_date)) {
      setFormError("Pick a date on the calendar.");
      return;
    }

    setSaving(true);
    try {
      if (isEditing && form.id) {
        await updateAttendanceHoliday(form.id, { holiday_name, holiday_date });
        onNotify?.("Holiday updated.", { variant: "success" });
      } else {
        await createAttendanceHoliday({ holiday_name, holiday_date });
        onNotify?.("Holiday saved.", { variant: "success" });
      }
      const holidayYear = Number(holiday_date.slice(0, 4));
      if (holidayYear !== selectedYear) {
        setSelectedYear(holidayYear);
      } else {
        loadHolidays();
      }
      resetForm();
    } catch (err) {
      setFormError(err.message || "Failed to save holiday.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteAttendanceHoliday(deleteTarget.id);
      onNotify?.("Holiday removed.", { variant: "success" });
      if (form.id === deleteTarget.id) resetForm();
      setDeleteTarget(null);
      loadHolidays();
    } catch (err) {
      onNotify?.(err.message, { variant: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const shiftYear = (delta) => {
    const next = sortedYears[yearIndex + delta];
    if (next != null) setSelectedYear(next);
  };

  return (
    <section id="att-holidays" className="att-panel att-panel--holidays">
      <div className="att-panel__head att-panel__head--compact">
        <div className="att-panel__icon" aria-hidden>
          <CalendarDays size={20} />
        </div>
        <div>
          <h3 className="att-panel__title">Organisation holidays</h3>
          <p className="att-panel__hint">Mark days off for the whole team — browse by year below.</p>
        </div>
      </div>

      <div className="att-panel__body att-panel__body--holidays">
        <div className="att-hol-year-bar">
          <button
            type="button"
            className="att-hol-year-bar__nav"
            onClick={() => shiftYear(1)}
            disabled={yearIndex >= sortedYears.length - 1}
            aria-label="Previous year"
          >
            <ChevronLeft size={18} />
          </button>

          <div className="att-hol-year-bar__pills" role="tablist" aria-label="Holiday year">
            {sortedYears.map((year) => (
              <button
                key={year}
                type="button"
                role="tab"
                aria-selected={year === selectedYear}
                className={`att-hol-year-pill${year === selectedYear ? " att-hol-year-pill--active" : ""}${
                  year === CURRENT_YEAR ? " att-hol-year-pill--current" : ""
                }`}
                onClick={() => setSelectedYear(year)}
              >
                {year}
                {year === CURRENT_YEAR ? <span className="att-hol-year-pill__dot" aria-hidden /> : null}
              </button>
            ))}
          </div>

          <button
            type="button"
            className="att-hol-year-bar__nav"
            onClick={() => shiftYear(-1)}
            disabled={yearIndex <= 0}
            aria-label="Next year"
          >
            <ChevronRight size={18} />
          </button>

          <span className="att-hol-year-bar__count">
            {loading ? "…" : rows.length} day{rows.length === 1 ? "" : "s"} off
          </span>
        </div>

        <form className={`att-hol-composer${isEditing ? " att-hol-composer--editing" : ""}`} onSubmit={handleSubmit}>
          <div className="att-hol-composer__label">
            {isEditing ? (
              <>
                <Pencil size={14} aria-hidden />
                Editing holiday
              </>
            ) : (
              <>
                <Plus size={14} aria-hidden />
                Add a holiday
              </>
            )}
          </div>

          <div className="att-hol-composer__fields">
            <GlobalTextField
              label="Name"
              value={form.holiday_name}
              onChange={(v) => setForm((p) => ({ ...p, holiday_name: v }))}
              placeholder="Republic Day, Diwali…"
              required
            />
            <GlobalTextField
              label="Date"
              type="date"
              value={form.holiday_date}
              onChange={(v) => setForm((p) => ({ ...p, holiday_date: v }))}
              required
            />
          </div>

          <div className="att-hol-composer__actions">
            {isEditing ? (
              <button
                type="button"
                className="att-hol-composer__cancel"
                onClick={resetForm}
                disabled={saving}
                aria-label="Cancel edit"
              >
                <X size={16} />
              </button>
            ) : null}
            <GlobalButton type="submit" variant="primary" disabled={saving}>
              {saving ? "Saving…" : isEditing ? "Update" : "Add"}
            </GlobalButton>
          </div>

          {formError ? (
            <p className="att-hol-composer__error" role="alert">
              {formError}
            </p>
          ) : null}
        </form>

        <div className="att-hol-timeline">
          {loading ? (
            <p className="att-empty">Loading holidays…</p>
          ) : rows.length === 0 ? (
            <div className="att-hol-empty">
              <div className="att-hol-empty__icon" aria-hidden>
                <Sparkles size={26} strokeWidth={1.5} />
              </div>
              <p>No holidays in {selectedYear} yet.</p>
              <span>Add one above — it will show up on the timeline.</span>
            </div>
          ) : (
            monthGroups.map(([monthIndex, monthRows]) => (
              <div key={monthIndex} className="att-hol-month">
                <h4 className="att-hol-month__label">{MONTH_NAMES[monthIndex]}</h4>
                <ul className="att-hol-list">
                  {monthRows.map((row) => {
                    const parsed = parseHolidayDate(row.holiday_date);
                    const isActive = form.id === row.id && isEditing;
                    return (
                      <li
                        key={row.id}
                        className={`att-hol-card${isActive ? " att-hol-card--active" : ""}`}
                      >
                        <div className="att-hol-card__date" aria-hidden>
                          <span className="att-hol-card__day">{parsed?.day ?? "—"}</span>
                          <span className="att-hol-card__month">{parsed?.month ?? ""}</span>
                        </div>
                        <div className="att-hol-card__body">
                          <h5>{row.holiday_name}</h5>
                          <p>{parsed?.long ?? row.holiday_date}</p>
                        </div>
                        <div className="att-hol-card__actions">
                          <GlobalTableActionButton action="edit" onClick={() => handleEdit(row)} />
                          <GlobalTableActionButton action="delete" onClick={() => setDeleteTarget(row)} />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>
      </div>

      <GlobalModal
        open={Boolean(deleteTarget)}
        title="Remove holiday?"
        onClose={() => !deleting && setDeleteTarget(null)}
        footer={
          <>
            <GlobalButton
              type="button"
              variant="ghost"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
            >
              Cancel
            </GlobalButton>
            <GlobalButton type="button" variant="danger" onClick={confirmDelete} disabled={deleting}>
              {deleting ? "Removing…" : "Remove"}
            </GlobalButton>
          </>
        }
      >
        <p>
          Remove <strong>{deleteTarget?.holiday_name}</strong>
          {deleteTarget?.holiday_date ? (
            <>
              {" "}
              on <strong>{parseHolidayDate(deleteTarget.holiday_date)?.long}</strong>
            </>
          ) : null}
          ?
        </p>
      </GlobalModal>
    </section>
  );
}
